import { createApp, type App } from "vue";
import type { Router } from "vue-router";
// @ts-ignore
import ClientMonitor from 'skywalking-client-js';
import Antd from "ant-design-vue";
import "@/assets/css/reset.css";
import "@/assets/css/common.css";
import "ant-design-vue/dist/antd.css";
import _App from "./App.vue";
import router from "./router";
import store from "./store";
import { globalAxios } from "./request";
import permission from "./directives/permission";
import WujieVue from "wujie-vue3";
import VueViewer from "v-viewer";
import "viewerjs/dist/viewer.css";
import { initI18n } from "./locales/i18n";
// @ts-ignore
import Yzsf2eAenderCore from "@yzsf2e/render-core";
import "@yzsf2e/render-core/dist/style.css";
import {initSensors} from '@/utils/sensors.js'

// 类型声明
declare global {
  interface Window {
    ww: any;
    ClientMonitor: typeof ClientMonitor;
  }
}
if (
  window.location.hash != "/" &&
  window.location.hash.indexOf("/child-container") < 0
) {
  window.location.hash = "/";
}
// 常量定义
const ENABLE_SPA = false;

// 初始化 SkyWalking 监控
function initSkyWalking(): void {
  try {
    window.ClientMonitor = ClientMonitor;
    
    ClientMonitor.register({
      service: __APP_SERVICE_NAME__,
      pagePath: window.location.href,
      serviceVersion: __APP_VERSION__,
      enableSPA: ENABLE_SPA,
    });
  } catch (error) {
    console.error('SkyWalking 初始化失败:', error);
  }
}

// 错误报告函数
function reportFrameErrors(error: Error | ErrorEvent, errorType: string): void {
  try {
    // 创建错误副本避免修改原始错误对象
    const errorToReport = new Error(error.message ? `${errorType} ${error.message}` : errorType);
    
    // 获取当前活跃标签页名称
    const activeTab = document.querySelector('.yzs-tabs-container .ant-tabs-tab.ant-tabs-tab-active');
    const pageName = `${activeTab?.textContent?.trim() || '工作台'}`;
    
    if (errorToReport.stack) {
      errorToReport.stack += ` [${window.location.href}]`
    }

    ClientMonitor.reportFrameErrors({
      service: __APP_SERVICE_NAME__,
      pageName: pageName,
      pagePath: window.location.href,
      serviceVersion: __APP_VERSION__,
    }, errorToReport);
  } catch (reportError) {
    console.error('错误报告失败:', reportError);
  }
}

// 设置路由监控
function setupRouterMonitoring(router: Router): void {
  router.beforeEach((to, from, next) => {
    try {
      ClientMonitor.setPerformance({
        pagePath: location.href,
      });
    } catch (error) {
      console.error('路由监控设置失败:', error);
    }
    next();
  });
}

// 设置全局错误处理
function setupErrorHandling(app: App): void {
  // Vue3 全局错误处理
  app.config.errorHandler = (error: unknown) => {
    console.error('Vue 全局错误:', error);
    if (error instanceof Error) {
      reportFrameErrors(error, '[Vue代码报错]');
    }
  };

  // 全局 JavaScript 错误监听
  window.addEventListener('error', (event: ErrorEvent) => {
    const errorType = event.filename ? '[JS代码报错]' : '[资源加载错误]';
    reportFrameErrors(event, errorType);
  });

  // Promise 未捕获错误
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    console.error('未捕获的 Promise 错误:', event.reason);
    const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
    reportFrameErrors(error, '[Promise报错]');
  });
}

// 注册插件
async function registerPlugins(app: App): Promise<void> {
  // 使用 Ant Design Vue
  app.use(Antd);
  
  // 使用渲染核心
  app.use(Yzsf2eAenderCore);

  // 使用无界微前端
  app.use(WujieVue);

  // 使用全局 axios
  app.use(globalAxios);

  // 使用 Vuex
  app.use(store);

  // 使用路由
  app.use(router);

  // 使用图片查看器
  app.use(VueViewer);

  // 初始化国际化
  await initI18n(app);
  
  // 等待路由准备就绪
  await router.isReady();

  // 注册自定义指令
  app.directive("permission", permission);
}

// 主启动函数
async function bootstrap(): Promise<void> {
  try {
    const app = createApp(_App);

    // 初始化监控
    initSkyWalking();

    // 设置路由监控
    setupRouterMonitoring(router);

    // 注册所有插件
    await registerPlugins(app);

    // 设置错误处理
    setupErrorHandling(app);

    // 挂载应用
    app.mount("#app");
    initSensors()

    console.log(`${__APP_SERVICE_NAME__} 应用启动成功`);
  } catch (error) {
    console.error('应用启动失败:', error);
    
    // 即使应用启动失败，也尝试报告错误
    if (error instanceof Error) {
      reportFrameErrors(error, '[应用启动失败]');
    }
  }
}

// 启动应用
bootstrap();